import React, { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrgCurrency } from '../../../../hooks/useOrgCurrency.js';
import { Shield, Building, ChevronDown, Check, AlertTriangle, Info, Plus } from 'lucide-react';
import CoverageModelSelector from '../../../insurance/shared/CoverageModelSelector';
import ContractForm from '../../../insurance/forms/ContractForm.jsx';
import { SINGLE_VEHICLE_CONTRACT_TYPES } from '../../../../utils/insuranceStatus';

// Canonical contract types — aligned with backend enum insurance_contract.contract_type
// and with CONTRACT_TYPE_LABELS in InsuranceUserView.jsx. Bris / rachat / autre
// viendront post-salon via le vrai formulaire ContractForm.
// Labels reuse insurance.contractType.* (already translated, same codes).
const CONTRACT_TYPES = [
  { code: 'motor_fleet', category: 'fleet' },
  { code: 'motor_single', category: 'individual' },
];

const AUTO_CONTRACT_CODES = new Set(['motor_fleet', 'motor_single']);

/**
 * GUARANTEE_BLOCKS — DEPRECATED: Now loaded from API via CoverageModelSelector.
 * Kept as empty array for backward compatibility with any code that references it.
 */
const GUARANTEE_BLOCKS = [];

/**
 * Payment frequency options
 */
const PAYMENT_FREQUENCIES = ['annual', 'semi_annual', 'quarterly', 'monthly'];

/**
 * Payment methods
 */
const PAYMENT_METHODS = ['direct_debit', 'transfer', 'check', 'card', 'other'];

/**
 * StepInsurance - Step 5 of VehicleLifecycleWorkflow
 *
 * Enhanced insurance workflow with:
 * - 8 contract types (fleet/individual)
 * - Guarantee block selection
 * - Premium, franchise, payment details
 */
// Guarantee codes that share the per-vehicle franchise (damage, theft, fire, glass)
// Must match actual DB codes from guarantee_template / coverage_model_guarantee
const FRANCHISE_GUARANTEE_CODES = ['DAMAGE_ALL_RISK', 'DAMAGE_COLLISION', 'THEFT', 'FIRE', 'GLASS'];

function StepInsurance({
  formData,
  updateFormData,
  insuranceContracts,
  vehicleInsurances = [],
  insuranceCompanies = [],
  onContractCreated,
}) {
  const { t } = useTranslation();
  // Devise de la filiale active - un seul outil monetaire.
  const { moneyScope, scopeSymbol } = useOrgCurrency();
  // Accordion state: which panel is open (1,2,3) and which are done
  const [openPanel, setOpenPanel] = useState(() => {
    // Auto-detect initial panel from existing data
    try {
      const assignments = JSON.parse(formData.insurance_assignments || '[]');
      if (assignments.length > 0 && assignments[0].existingContractId) {
        return 3; // Contract selected, show coverage
      }
      if (assignments.length > 0) return 2; // Type selected, show contract
    } catch {}
    return 1;
  });

  // Modal-over-modal: open ContractForm on top of the wizard when user wants
  // to create a new contract. Keeping it local means the wizard modal stays
  // mounted behind — zero navigation, zero lost context. See /docs/adr/ADR-009.
  const [showContractForm, setShowContractForm] = useState(false);
  const [pendingContractType, setPendingContractType] = useState(null);

  // Get selected insurance assignments
  const insuranceAssignments = useMemo(() => {
    try {
      return JSON.parse(formData.insurance_assignments || '[]');
    } catch {
      return [];
    }
  }, [formData.insurance_assignments]);

  // Coverage model selection (replaces legacy guarantee JSON)
  const coverageModelValue = useMemo(() => {
    try {
      return JSON.parse(formData.insurance_coverage_model || '{}');
    } catch {
      return {};
    }
  }, [formData.insurance_coverage_model]);

  const handleCoverageModelChange = useCallback((val) => {
    updateFormData('insurance_coverage_model', JSON.stringify(val));

    // If user changed deductible on a franchise-linked guarantee, sync back to assignment
    if (val.overrides && insuranceAssignments.length > 0) {
      const linkedCode = FRANCHISE_GUARANTEE_CODES.find(c => val.overrides[c]?.deductible_amount !== undefined);
      if (linkedCode) {
        const newFranchise = val.overrides[linkedCode].deductible_amount;
        // Sync to first auto contract assignment
        const updated = insuranceAssignments.map((a, i) => {
          if (i === 0 && AUTO_CONTRACT_CODES.has(a.type)) {
            return { ...a, franchise: newFranchise != null ? String(newFranchise) : '' };
          }
          return a;
        });
        updateFormData('insurance_assignments', JSON.stringify(updated));
      }
    }
  }, [updateFormData, insuranceAssignments]);

  // (FRANCHISE_GUARANTEE_CODES moved above hooks for TDZ safety)

  // Legacy: keep selectedGuarantees for backward compat count display
  const selectedGuarantees = useMemo(() => {
    try {
      return JSON.parse(formData.insurance_guarantees || '["rc"]');
    } catch {
      return ['rc'];
    }
  }, [formData.insurance_guarantees]);

  // Build the set of contract IDs that already cover an active vehicle.
  // Used to filter out motor_single contracts whose single slot is already taken.
  const contractsWithActiveVI = useMemo(() => {
    const s = new Set();
    for (const vi of vehicleInsurances || []) {
      if (!vi.removed_at) s.add(vi.contract_id);
    }
    return s;
  }, [vehicleInsurances]);

  // Contracts eligible for each picked contract type:
  //  - status === 'active'
  //  - contract_type matches the assignment type
  //  - motor_single: exclude contracts already linked to a vehicle
  //    (swapping the covered vehicle mid-policy is a post-salon flow,
  //    aligned with InsuranceVehiclePicker.singleSlotTaken)
  const eligibleContractsFor = useCallback((typeCode) => {
    if (!insuranceContracts?.length || !typeCode) return [];
    return insuranceContracts
      .filter(c => (c.status || '').toLowerCase() === 'active')
      .filter(c => c.contract_type === typeCode)
      .filter(c => {
        if (!SINGLE_VEHICLE_CONTRACT_TYPES.has(typeCode)) return true;
        return !contractsWithActiveVI.has(c.id);
      })
      .sort((a, b) => (a.insurer_name || '').localeCompare(b.insurer_name || ''));
  }, [insuranceContracts, contractsWithActiveVI]);

  // All active contracts (any type) — used for live summary lookup
  const activeContractsAll = useMemo(() => {
    return (insuranceContracts || []).filter(c => (c.status || '').toLowerCase() === 'active');
  }, [insuranceContracts]);

  // Group contract types by category
  const fleetTypes = CONTRACT_TYPES.filter(t => t.category === 'fleet');
  const individualTypes = CONTRACT_TYPES.filter(t => t.category === 'individual');

  // Accordion helpers
  const panelHasType = insuranceAssignments.length > 0;
  const firstAssignment = insuranceAssignments[0];
  const panelHasContract = panelHasType && !!firstAssignment?.existingContractId;
  const panelHasCoverage = !!coverageModelValue.coverageModelId;

  // Open ContractForm modal for the currently active assignment type.
  const handleOpenContractForm = useCallback(() => {
    const a = insuranceAssignments[0];
    setPendingContractType(a?.type || null);
    setShowContractForm(true);
  }, [insuranceAssignments]);

  // Called when ContractForm saves — auto-select the new contract and advance.
  // onContractCreated (from parent) is responsible for refreshing the shared
  // insuranceContracts list so the new record appears in the eligible list.
  const handleContractFormSuccess = useCallback(async (newContract) => {
    setShowContractForm(false);
    if (!newContract?.id) return;
    // Notify parent so it refetches + injects the new contract into its state.
    if (onContractCreated) {
      try { await onContractCreated(newContract); } catch (err) { console.error('onContractCreated error:', err); }
    }
    // Auto-select on the active assignment; only typeCode we know is pendingContractType
    const targetType = pendingContractType || insuranceAssignments[0]?.type;
    if (!targetType) return;
    const updated = insuranceAssignments.map(a =>
      a.type === targetType
        ? { ...a, existingContractId: String(newContract.id), createNew: false }
        : a
    );
    updateFormData('insurance_assignments', JSON.stringify(updated));
    setTimeout(() => setOpenPanel(3), 200);
  }, [pendingContractType, insuranceAssignments, updateFormData, onContractCreated]);

  const getPanelState = (n) => {
    if (n === 1) return panelHasType ? 'done' : (openPanel === 1 ? 'active' : 'pending');
    if (n === 2) return panelHasContract ? 'done' : (openPanel === 2 && panelHasType ? 'active' : 'locked');
    if (n === 3) return openPanel === 3 && panelHasContract ? 'active' : (!panelHasContract ? 'locked' : 'pending');
    return 'pending';
  };

  // Live summary (looks up the chosen contract across all active contracts)
  const liveSummary = useMemo(() => {
    const a = insuranceAssignments[0];
    const premium = parseFloat(a?.premium) || 0;
    const franchise = parseFloat(a?.franchise) || 0;
    const glass = coverageModelValue.overrides?.GLASS?.deductible_amount ?? 0;
    const contract = activeContractsAll.find(c => String(c.id) === a?.existingContractId);
    return {
      premium, franchise, glass,
      monthly: Math.round(premium / 12),
      contractName: contract?.insurer_name || '--',
      modelName: coverageModelValue.coverageModelName || '--',
    };
  }, [insuranceAssignments, coverageModelValue, activeContractsAll]);

  // Auto-advance when type selected
  const handleTypeSelect = (contractType) => {
    const existing = insuranceAssignments.find(a => a.type === contractType.code);
    if (existing) {
      removeContractAssignment(contractType.code);
      return;
    }
    addContractAssignment(contractType);
    setTimeout(() => setOpenPanel(2), 150);
  };

  // Auto-advance when contract picked
  const handleContractPick = (typeCode, contractId) => {
    updateAssignment(typeCode, 'existingContractId', contractId);
    setTimeout(() => setOpenPanel(3), 200);
  };

  // Add contract assignment
  const addContractAssignment = (contractType) => {
    const existing = insuranceAssignments.find(a => a.type === contractType.code);
    if (existing) return;

    const newAssignment = {
      type: contractType.code,
      existingContractId: '',
      createNew: false,
      customDescription: '',
      premium: '',
      franchise: '',
      paymentFrequency: 'annual',
      paymentMethod: 'direct_debit',
      paymentDay: '5'
    };

    const updated = [...insuranceAssignments, newAssignment];
    updateFormData('insurance_assignments', JSON.stringify(updated));
  };

  // Remove contract assignment
  const removeContractAssignment = (typeCode) => {
    const updated = insuranceAssignments.filter(a => a.type !== typeCode);
    updateFormData('insurance_assignments', JSON.stringify(updated));
  };

  // Update a specific assignment — propagates franchise to coverage model guarantees
  const updateAssignment = (typeCode, field, value) => {
    const updated = insuranceAssignments.map(a => {
      if (a.type === typeCode) {
        return { ...a, [field]: value };
      }
      return a;
    });
    updateFormData('insurance_assignments', JSON.stringify(updated));

    // When franchise changes, propagate to coverage model overrides for damage/theft/fire
    if (field === 'franchise' && coverageModelValue.coverageModelId) {
      const franchiseVal = parseFloat(value) || null;
      const newOverrides = { ...(coverageModelValue.overrides || {}) };
      for (const code of FRANCHISE_GUARANTEE_CODES) {
        if (!newOverrides[code]) newOverrides[code] = {};
        newOverrides[code].deductible_amount = franchiseVal;
      }
      updateFormData('insurance_coverage_model', JSON.stringify({
        ...coverageModelValue,
        overrides: newOverrides,
      }));
    }
  };

  // Toggle guarantee
  const toggleGuarantee = (code) => {
    if (code === 'rc') return; // RC is required
    const updated = selectedGuarantees.includes(code)
      ? selectedGuarantees.filter(g => g !== code)
      : [...selectedGuarantees, code];
    updateFormData('insurance_guarantees', JSON.stringify(updated));
  };

  // Inline accordion header+body helper (avoids TDZ issues with nested component)
  const renderAccPanel = (n, title, subtitle, children) => {
    const state = getPanelState(n);
    const isOpen = openPanel === n && state !== 'locked';
    const borderColor = state === 'done' ? 'border-green-400' : state === 'active' ? 'border-amber-400' : 'border-gray-200';
    const headerBg = state === 'done' ? 'bg-green-50' : state === 'active' ? 'bg-amber-50' : 'bg-gray-50';
    const badgeCls = state === 'done' ? 'bg-green-500 text-white' : state === 'active' ? 'bg-amber-500 text-white shadow-[0_0_0_3px_rgba(245,158,11,0.2)]' : 'bg-gray-200 text-gray-400';

    return (
      <div key={n} className={`border-2 rounded-xl overflow-hidden mb-2 transition-colors ${borderColor} ${state === 'locked' ? 'opacity-40 pointer-events-none' : ''}`}>
        <button
          type="button"
          onClick={() => state !== 'locked' && setOpenPanel(isOpen ? 0 : n)}
          className={`w-full flex items-center gap-3 px-4 py-3 ${headerBg} hover:brightness-95 transition-all`}
        >
          <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${badgeCls}`}>
            {state === 'done' ? <Check className="w-4 h-4" /> : n}
          </span>
          <div className="flex-1 text-left">
            <h3 className="font-semibold text-gray-900 text-[15px]">{title}</h3>
            <p className="text-xs text-gray-500">{subtitle}</p>
          </div>
          <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
        {isOpen && (
          <div className="px-4 pb-4 pt-2">{children}</div>
        )}
      </div>
    );
  };

  // Assurance incluse dans le contrat de leasing → bannière info, étape non bloquante
  const leasingIncludesInsurance = useMemo(() => {
    if (formData.acquisition_type !== 'lease') return false;
    try {
      return !!JSON.parse(formData.financing_options || '{}').insurance;
    } catch {
      return false;
    }
  }, [formData.acquisition_type, formData.financing_options]);

  return (
    <div className="space-y-2">
      <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
        <Shield className="w-5 h-5 text-amber-500" />
        {t('vehicles.wizard.insurance.title')}
      </h3>

      {leasingIncludesInsurance && (
        <div className="mb-3 p-3 rounded-xl border-2 border-blue-200 bg-blue-50 flex items-start gap-2">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-900">
            <p className="font-semibold">{t('vehicles.wizard.insurance.leasingIncludesTitle')}</p>
            <p className="text-xs text-blue-700 mt-0.5">
              {t('vehicles.wizard.insurance.leasingIncludesHint')}
            </p>
          </div>
        </div>
      )}

      {/* ═══════ PANEL 1: Type de contrat ═══════ */}
      {renderAccPanel(1, t('vehicles.wizard.insurance.panel1Title'),
        panelHasType ? t(`insurance.contractType.${firstAssignment?.type}`) : t('vehicles.wizard.insurance.panel1Subtitle'),
        <div className="grid grid-cols-2 gap-4">
          {/* Fleet */}
          <div>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2 flex items-center gap-1">
              <Building className="w-3 h-3" /> {t('vehicles.wizard.insurance.categoryFleet')}
            </p>
            <div className="space-y-1.5">
              {fleetTypes.map(type => {
                const isOn = insuranceAssignments.some(a => a.type === type.code);
                return (
                  <button key={type.code} type="button" onClick={() => handleTypeSelect(type)}
                    className={`w-full p-2.5 border-2 rounded-xl text-left transition-all ${isOn ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-amber-300 hover:bg-amber-50'}`}>
                    <span className="font-medium text-gray-800 text-sm">{t(`insurance.contractType.${type.code}`)}</span>
                    <span className="text-[11px] text-gray-500 block">{t(`vehicles.wizard.insurance.contractTypeDescriptions.${type.code}`)}</span>
                    {isOn && <Check className="w-4 h-4 text-green-600 absolute top-2 right-2" />}
                  </button>
                );
              })}
            </div>
          </div>
          {/* Individual */}
          <div>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2 flex items-center gap-1">
              <Shield className="w-3 h-3" /> {t('vehicles.wizard.insurance.categoryIndividual')}
            </p>
            <div className="space-y-1.5">
              {individualTypes.map(type => {
                const isOn = insuranceAssignments.some(a => a.type === type.code);
                return (
                  <button key={type.code} type="button" onClick={() => handleTypeSelect(type)}
                    className={`w-full p-2.5 border-2 rounded-xl text-left transition-all ${isOn ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-amber-300 hover:bg-amber-50'}`}>
                    <span className="font-medium text-gray-800 text-sm">{t(`insurance.contractType.${type.code}`)}</span>
                    <span className="text-[11px] text-gray-500 block">{t(`vehicles.wizard.insurance.contractTypeDescriptions.${type.code}`)}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ═══════ PANEL 2: Contrat d'assurance ═══════ */}
      {renderAccPanel(2, t('vehicles.wizard.insurance.panel2Title'),
        panelHasContract
          ? (activeContractsAll.find(c => String(c.id) === firstAssignment?.existingContractId)?.insurer_name || t('vehicles.wizard.insurance.contractSelected'))
          : t('vehicles.wizard.insurance.panel2Subtitle'),
        <div>
        {insuranceAssignments.map(assignment => {
          const typeInfo = CONTRACT_TYPES.find(ct => ct.code === assignment.type);
          const eligible = eligibleContractsFor(assignment.type);
          const isSingle = SINGLE_VEHICLE_CONTRACT_TYPES.has(assignment.type);
          return (
            <div key={assignment.type} className="space-y-3">
              {/* Existing contracts as radio cards — single source of truth.
                  Pour créer un nouveau contrat, le bouton ouvre le modal
                  ContractForm par-dessus le wizard (modal-over-modal).
                  Aucune navigation, l'utilisateur revient pile sur ce step. */}
              <div className="space-y-2">
                {eligible.map(contract => (
                  <label key={contract.id}
                    className={`flex items-center gap-3 p-3 border-2 rounded-xl cursor-pointer transition-all ${
                      String(contract.id) === assignment.existingContractId ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}`}
                    onClick={() => handleContractPick(assignment.type, String(contract.id))}>
                    <input type="radio" name={`ctr_${assignment.type}`} readOnly
                      checked={String(contract.id) === assignment.existingContractId}
                      className="w-4 h-4 text-blue-600" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm text-gray-900">{contract.insurer_name}</span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold bg-green-100 text-green-700">{t('vehicles.wizard.insurance.activeBadge')}</span>
                      </div>
                      <p className="text-[11px] text-gray-500 truncate">
                        {t('vehicles.wizard.insurance.contractSummaryLine', {
                          policyNumber: contract.policy_number,
                          type: typeInfo ? t(`insurance.contractType.${typeInfo.code}`) : contract.contract_type,
                          expiry: contract.expiry_date || '--',
                        })}
                      </p>
                    </div>
                    {contract.total_premium_ttc ? (
                      <span className="text-sm font-bold text-gray-600 whitespace-nowrap">{moneyScope(contract.total_premium_ttc)}</span>
                    ) : null}
                  </label>
                ))}
                {eligible.length === 0 && (
                  <p className="text-xs text-amber-600 p-2 bg-amber-50 rounded-lg border border-amber-200">
                    {isSingle
                      ? t('vehicles.wizard.insurance.noEligibleSingle')
                      : t('vehicles.wizard.insurance.noEligibleFleet')}
                  </p>
                )}

                {/* + Créer un nouveau contrat — ouvre ContractForm en modal-over-modal */}
                <button
                  type="button"
                  onClick={handleOpenContractForm}
                  className="w-full flex items-center justify-center gap-2 p-3 border-2 border-dashed border-blue-300 rounded-xl text-blue-700 hover:border-blue-500 hover:bg-blue-50 transition-all text-sm font-medium"
                >
                  <Plus className="w-4 h-4" />
                  {t('vehicles.wizard.insurance.createContract')}
                </button>
              </div>

              {/* Vehicle cost fields (visible once contract chosen) */}
              {assignment.existingContractId && (
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <p className="text-xs font-semibold text-gray-600 mb-3">{t('vehicles.wizard.insurance.vehicleConditionsTitle')}</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[11px] font-medium text-gray-500 mb-1">{t('vehicles.wizard.insurance.annualPremiumIncl')}</label>
                      <div className="relative">
                        <input type="number" value={assignment.premium}
                          onChange={(e) => updateAssignment(assignment.type, 'premium', e.target.value)}
                          placeholder="0" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-semibold focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-gray-400">{t('vehicles.wizard.insurance.perYearSuffix', { symbol: scopeSymbol })}</span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-[11px] font-medium text-gray-500 mb-1">{t('vehicles.wizard.insurance.damageDeductible')}</label>
                      <div className="relative">
                        <input type="number" value={assignment.franchise}
                          onChange={(e) => updateAssignment(assignment.type, 'franchise', e.target.value)}
                          placeholder="0" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-semibold focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-gray-400">{scopeSymbol}</span>
                      </div>
                      <p className="text-[10px] text-gray-400 mt-1">{t('vehicles.wizard.insurance.damageDeductibleHint')}</p>
                    </div>
                  </div>
                  <details className="mt-2">
                    <summary className="text-[11px] text-gray-400 cursor-pointer hover:text-gray-600">{t('vehicles.wizard.insurance.paymentDetailsToggle')}</summary>
                    <div className="grid grid-cols-3 gap-2 mt-2">
                      <div>
                        <label className="block text-[10px] text-gray-500 mb-1">{t('vehicles.wizard.insurance.paymentFrequencyLabel')}</label>
                        <select value={assignment.paymentFrequency} onChange={(e) => updateAssignment(assignment.type, 'paymentFrequency', e.target.value)}
                          className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs">
                          {PAYMENT_FREQUENCIES.map(value => <option key={value} value={value}>{t(`vehicles.wizard.insurance.paymentFrequencies.${value}`)}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-[10px] text-gray-500 mb-1">{t('vehicles.wizard.insurance.paymentMethodLabel')}</label>
                        <select value={assignment.paymentMethod} onChange={(e) => updateAssignment(assignment.type, 'paymentMethod', e.target.value)}
                          className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs">
                          {PAYMENT_METHODS.map(value => <option key={value} value={value}>{t(`vehicles.wizard.insurance.paymentMethods.${value}`)}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-[10px] text-gray-500 mb-1">{t('vehicles.wizard.insurance.paymentDayLabel')}</label>
                        <input type="number" value={assignment.paymentDay} min="1" max="28"
                          onChange={(e) => updateAssignment(assignment.type, 'paymentDay', e.target.value)}
                          className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs" />
                      </div>
                    </div>
                  </details>
                </div>
              )}
            </div>
          );
        })}
        </div>
      )}

      {/* ═══════ PANEL 3: Formule de couverture ═══════ */}
      {renderAccPanel(3, t('vehicles.wizard.insurance.panel3Title'),
        panelHasCoverage ? coverageModelValue.coverageModelName : t('vehicles.wizard.insurance.panel3Subtitle'),
        <div>
        <CoverageModelSelector
          value={coverageModelValue}
          onChange={handleCoverageModelChange}
        />

        {/* Live summary */}
        {liveSummary.premium > 0 && (
          <div className="mt-4 p-4 rounded-xl border border-blue-200" style={{ background: 'linear-gradient(135deg, #eff6ff, #dbeafe)' }}>
            <p className="text-[13px] font-semibold text-blue-900 mb-2">
              {liveSummary.contractName} &middot; {liveSummary.modelName}
            </p>
            <div className="flex gap-5">
              <div>
                <p className="text-[9px] text-blue-500 uppercase font-bold">{t('vehicles.wizard.insurance.premiumPerYear')}</p>
                <p className="text-sm font-bold text-blue-900">{moneyScope(liveSummary.premium)}</p>
              </div>
              <div>
                <p className="text-[9px] text-blue-500 uppercase font-bold">{t('vehicles.wizard.insurance.premiumPerMonth')}</p>
                <p className="text-sm font-bold text-blue-900">{moneyScope(liveSummary.monthly)}</p>
              </div>
              <div>
                <p className="text-[9px] text-blue-500 uppercase font-bold">{t('vehicles.wizard.insurance.deductible')}</p>
                <p className="text-sm font-bold text-blue-900">{moneyScope(liveSummary.franchise)}</p>
              </div>
              {liveSummary.glass > 0 && (
                <div>
                  <p className="text-[9px] text-amber-600 uppercase font-bold">{t('vehicles.wizard.insurance.glassBreakage')}</p>
                  <p className="text-sm font-bold text-amber-700">{moneyScope(liveSummary.glass)}</p>
                </div>
              )}
            </div>
          </div>
        )}
        </div>
      )}

      {/* No Insurance Warning */}
      {insuranceAssignments.length === 0 && (
        <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-amber-900 text-sm">{t('vehicles.wizard.insurance.noContractTitle')}</h4>
            <p className="text-xs text-amber-700 mt-1">
              {t('vehicles.wizard.insurance.noContractHint')}
            </p>
          </div>
        </div>
      )}

      {/* Modal-over-modal: ContractForm flotte par-dessus le wizard véhicule.
          Le wizard reste monté derrière — l'utilisateur ne perd jamais son contexte,
          il revient sur ce step 5 à la fermeture du modal. */}
      {showContractForm && (
        <ContractForm
          contract={null}
          lockedContractType={pendingContractType}
          onClose={() => setShowContractForm(false)}
          onSuccess={handleContractFormSuccess}
        />
      )}
    </div>
  );
}

export default StepInsurance;
